// Commit a weekly plan: materialize every filled slot into real
// scheduled_meals rows so the Plan calendar, prep notifications,
// family visibility, and CookMode all work unchanged.
//
// Book slots schedule normally (slug + owner). Concept slots have no
// recipe anywhere yet — they get a recognizable placeholder slug plus
// a CONCEPT PSEUDO-RECIPE in recipe_snapshot (steps: []). The calendar
// renders snapshot-first (calendarUtils.js), and MealDetailDrawer
// swaps COOK for GENERATE FULL RECIPE when it sees _concept.
//
// Re-commit is idempotent: this plan's still-'planned' meals are
// cleared first (by weekly_plan_id), so swapping a day and committing
// again never double-books the week. Cooked/skipped rows are history
// and stay.

import { supabase } from "./supabase";

export const SLOT_HOURS = {
  breakfast: { hour: 8, minute: 0 },
  lunch: { hour: 12, minute: 30 },
  dinner: { hour: 18, minute: 30 },
};

export function conceptPlaceholderSlug(planId, day, slot) {
  return `wk-${String(planId).slice(0, 8)}-d${day}-${slot}`;
}

// week_start is a DATE string ("2026-07-13"); build the local
// wall-clock Date for a day offset + meal slot.
export function slotDateTime(weekStartISO, day, slot) {
  const [y, m, d] = String(weekStartISO).split("-").map(Number);
  const t = SLOT_HOURS[slot] || SLOT_HOURS.dinner;
  return new Date(y, (m || 1) - 1, (d || 1) + Number(day || 0), t.hour, t.minute, 0, 0);
}

export function conceptPseudoRecipe({ concept, slug, servings, planId, day, slot }) {
  return {
    slug,
    title: concept.title,
    subtitle: concept.subtitle || "",
    cuisine: concept.cuisine || null,
    serves: servings,
    time: { prep: 0, cook: Number(concept.estMinutes) || 30 },
    steps: [],
    ingredients: (concept.estIngredients || []).map((e) => ({
      amount:
        e?.amount != null && e?.unit ? `${e.amount} ${e.unit}`
        : e?.amount != null ? String(e.amount)
        : "",
      item: e?.name || "",
      ...(e?.canonicalId ? { ingredientId: e.canonicalId } : {}),
    })),
    tags: [],
    _concept: {
      weeklyPlanId: planId,
      day,
      slot,
      costTier: concept.costTier ?? 2,
      priorityMode: concept.priorityMode || "chefs_choice",
      estPerServing: concept.estPerServing || null,
    },
  };
}

/**
 * @param {object} opts
 * @param {object}   opts.plan     — weekly_plans row (snake_case: id,
 *                                   week_start, household, slots)
 * @param {function} opts.schedule — useScheduledMeals().schedule
 * @returns {Promise<{ scheduled: number, failed: Array }>}
 */
export async function commitWeeklyPlan({ plan, schedule }) {
  if (!plan?.id || typeof schedule !== "function") {
    throw new Error("commitWeeklyPlan needs a plan row and schedule()");
  }
  const servings = Number(plan.household?.servings) || 2;

  // Idempotent re-commit: clear this plan's not-yet-cooked meals.
  // Realtime DELETE events keep useScheduledMeals' local state in sync.
  const { error: clearErr } = await supabase
    .from("scheduled_meals")
    .delete()
    .eq("weekly_plan_id", plan.id)
    .eq("status", "planned");
  if (clearErr) console.warn("[weeklyPlanCommit] clear:", clearErr.message);

  const filled = (Array.isArray(plan.slots) ? plan.slots : []).filter(
    (s) => s && s.enabled !== false && !s.error && s.source,
  );

  let scheduled = 0;
  const failed = [];
  for (const slot of filled) {
    const scheduledFor = slotDateTime(plan.week_start, slot.day, slot.slot).toISOString();
    try {
      if (slot.source === "book" && slot.recipeSlug) {
        await schedule({
          recipeSlug: slot.recipeSlug,
          recipeUserId: slot.recipeOwnerUserId || undefined,
          scheduledFor,
          servings,
          mealSlot: slot.slot,
          weeklyPlanId: plan.id,
        });
      } else if (slot.source === "new" && slot.concept?.title) {
        const slug = conceptPlaceholderSlug(plan.id, slot.day, slot.slot);
        await schedule({
          recipeSlug: slug,
          scheduledFor,
          servings,
          mealSlot: slot.slot,
          weeklyPlanId: plan.id,
          recipeSnapshot: conceptPseudoRecipe({
            concept: slot.concept,
            slug,
            servings,
            planId: plan.id,
            day: slot.day,
            slot: slot.slot,
          }),
        });
      } else {
        continue;
      }
      scheduled += 1;
    } catch (e) {
      console.warn("[weeklyPlanCommit] slot failed:", slot.day, slot.slot, e?.message || e);
      failed.push({ day: slot.day, slot: slot.slot, error: e?.message || String(e) });
    }
  }

  return { scheduled, failed };
}
